<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';

import { useProjectWrite } from '@/utils/project-write';
import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import PageState from '@/components/PageState.vue';
import { notify } from '@/services/notice';
import type { Project } from '@/types/domain';
import { errorMessage } from '@/utils/format';

interface PpapElement {
  id: number;
  elementNo: number;
  name: string;
  required: boolean;
  status: string;
  evidenceDocumentId: number | null;
  note: string | null;
  updatedByName: string | null;
  auto: { ready: boolean; detail: string } | null;
}

interface PpapPayload {
  package: {
    id: number; partNumber: string; submissionLevel: number; status: string;
    customerDecision: string | null; note: string | null; updatedAt: string;
  } | null;
  elements?: PpapElement[];
  summary?: { requiredTotal: number; covered: number; missing: number; inProgress: number };
  candidateDocuments?: Array<{ id: number; code: string | null; title: string }>;
}

const props = defineProps<{ project: Project }>();
const { access } = useSession();
/*
  能不能改 = 账号不是只读 且 这个项目在自己名下。
  所有项目都能看（Tony 2026-08-18 定的口径），收着的只有写。
*/
const canWrite = useProjectWrite(computed(() => props.project));

const data = ref<PpapPayload | null>(null);
const loading = ref(true);
const error = ref('');
const saving = ref(false);
const savingElementId = ref<number | null>(null);
const createLevel = ref('3');

const STATUS_LABEL: Readonly<Record<string, string>> = {
  missing: '缺失',
  in_progress: '准备中',
  ready: '就绪',
  not_applicable: '不适用',
};

/** 四个自动核对项之外的要素，模块内没有对应实体，只能人工 + 证据文件 */
const MODULE_LINKS: Readonly<Record<number, { label: string; path: string }>> = {
  2: { label: '打开变更管理', path: 'changes' },
  4: { label: '打开 DFMEA', path: 'dfmea' },
  6: { label: '打开 PFMEA', path: 'pfmea' },
  7: { label: '打开控制计划', path: 'control-plans' },
};

const percent = computed(() => {
  const summary = data.value?.summary;
  if (!summary?.requiredTotal) return 0;
  return Math.round((summary.covered / summary.requiredTotal) * 100);
});

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    data.value = await eqsApi.ppap(props.project.id) as unknown as PpapPayload;
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

async function createPackage(): Promise<void> {
  saving.value = true;
  try {
    data.value = await eqsApi.createPpap(props.project.id, {
      submissionLevel: Number(createLevel.value),
    }) as unknown as PpapPayload;
    notify('success', 'PPAP 包已建立', '18 个要素已按 AIAG 清单生成，可逐项确认。');
  } catch (caught) {
    notify('error', '创建失败', errorMessage(caught));
  } finally {
    saving.value = false;
  }
}

async function patchPackage(payload: Record<string, unknown>): Promise<void> {
  saving.value = true;
  try {
    data.value = await eqsApi.updatePpap(props.project.id, payload) as unknown as PpapPayload;
  } catch (caught) {
    notify('error', '保存失败', errorMessage(caught));
    await load();
  } finally {
    saving.value = false;
  }
}

async function patchElement(element: PpapElement, payload: Record<string, unknown>): Promise<void> {
  savingElementId.value = element.id;
  try {
    data.value = await eqsApi.updatePpapElement(
      props.project.id, element.id, payload,
    ) as unknown as PpapPayload;
  } catch (caught) {
    notify('error', `“${element.name}”未保存`, errorMessage(caught));
    await load();
  } finally {
    savingElementId.value = null;
  }
}

function onElementStatus(element: PpapElement, event: Event): void {
  void patchElement(element, { status: (event.target as HTMLSelectElement).value });
}

function onElementEvidence(element: PpapElement, event: Event): void {
  const value = (event.target as HTMLSelectElement).value;
  void patchElement(element, { evidenceDocumentId: value ? Number(value) : null });
}

function onElementNote(element: PpapElement, event: Event): void {
  const value = (event.target as HTMLInputElement).value.trim();
  if ((element.note ?? '') === value) return;
  void patchElement(element, { note: value || null });
}

onMounted(load);
</script>

<template>
  <header class="subpage-context">
    <p><span>PPAP / SUBMISSION</span>AIAG 18 要素逐项勾稽：能从系统核对的自动提示，勾「就绪」永远是人的动作。</p>
  </header>

  <PageState v-if="loading" state="loading" title="正在读取 PPAP 包" message="核对 DFMEA、PFMEA、控制计划与变更。" />
  <PageState v-else-if="error" state="error" title="PPAP 数据不可用" message="下方是接口返回的原因；重试仍失败请联系管理员。" :detail="error" @retry="load" />

  <section v-else-if="!data?.package" class="content-panel ppap-init">
    <h2>本项目还没有 PPAP 提交包</h2>
    <p>
      建立后会生成 AIAG 的 18 要素清单；DFMEA、PFMEA、控制计划、工程变更四项
      会自动对照系统里的实际数据给出提示，其余要素逐项人工确认并挂证据文件。
    </p>
    <div class="ppap-init-actions">
      <label>
        <span>提交等级</span>
        <select v-model="createLevel">
          <option value="1">等级 1 — 仅保证书（PSW）</option>
          <option value="2">等级 2 — PSW + 有限支持数据</option>
          <option value="3">等级 3 — PSW + 完整支持数据（默认）</option>
          <option value="4">等级 4 — 按客户指定</option>
          <option value="5">等级 5 — 供应商现场评审</option>
        </select>
      </label>
      <button class="button button-primary" type="button" :disabled="!canWrite || saving" @click="createPackage">
        {{ saving ? '正在建立…' : '建立 PPAP 包' }}
      </button>
    </div>
  </section>

  <template v-else>
    <section class="content-panel ppap-head">
      <div class="ppap-meta">
        <label>
          <span>提交等级</span>
          <select
            :value="String(data.package.submissionLevel)"
            :disabled="!canWrite || saving"
            @change="patchPackage({ submissionLevel: Number(($event.target as HTMLSelectElement).value) })"
          >
            <option v-for="level in [1, 2, 3, 4, 5]" :key="level" :value="String(level)">等级 {{ level }}</option>
          </select>
        </label>
        <label>
          <span>内部状态</span>
          <select
            :value="data.package.status"
            :disabled="!canWrite || saving"
            @change="patchPackage({ status: ($event.target as HTMLSelectElement).value })"
          >
            <option value="draft">草稿</option>
            <option value="in_progress">准备中</option>
            <option value="submitted">已提交客户</option>
            <option value="closed">已关闭</option>
          </select>
        </label>
        <label>
          <span>客户裁决</span>
          <select
            :value="data.package.customerDecision ?? ''"
            :disabled="!canWrite || saving"
            @change="patchPackage({ customerDecision: ($event.target as HTMLSelectElement).value || null })"
          >
            <option value="">未裁决</option>
            <option value="approved">批准</option>
            <option value="interim">临时批准</option>
            <option value="rejected">拒收</option>
          </select>
        </label>
      </div>
      <div class="ppap-readiness" role="img" :aria-label="`必交要素就绪 ${percent}%`">
        <div class="ppap-bar"><i :style="{ width: `${percent}%` }"></i></div>
        <b>{{ data.summary?.covered ?? 0 }} / {{ data.summary?.requiredTotal ?? 18 }} 必交要素就绪（含不适用）</b>
        <small v-if="data.summary?.missing">还缺 {{ data.summary.missing }} 项，准备中 {{ data.summary.inProgress }} 项</small>
      </div>
    </section>

    <section class="content-panel">
      <div class="data-table-wrap">
        <table class="data-table ppap-table">
          <thead>
            <tr>
              <th>#</th>
              <th>要素</th>
              <th title="系统内实际数据的自动核对；只做提示，不替人打勾">系统核对</th>
              <th>状态</th>
              <th>证据文件</th>
              <th>备注</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="element in data.elements"
              :key="element.id"
              :class="[`is-${element.status}`, { saving: savingElementId === element.id }]"
            >
              <td>{{ element.elementNo }}</td>
              <td class="ppap-name"><b>{{ element.name }}</b></td>
              <td class="ppap-auto">
                <template v-if="element.auto">
                  <i :class="element.auto.ready ? 'ok' : 'warn'">{{ element.auto.ready ? '✓' : '!' }}</i>
                  <span>{{ element.auto.detail }}</span>
                  <RouterLink
                    v-if="MODULE_LINKS[element.elementNo]"
                    class="text-link"
                    :to="`/projects/${project.id}/${MODULE_LINKS[element.elementNo].path}`"
                  >{{ MODULE_LINKS[element.elementNo].label }} →</RouterLink>
                </template>
                <span v-else class="ppap-manual">人工确认</span>
              </td>
              <td>
                <select
                  class="cell-input"
                  :value="element.status"
                  :disabled="!canWrite"
                  :aria-label="`${element.name} 的状态`"
                  @change="onElementStatus(element, $event)"
                >
                  <option v-for="(label, value) in STATUS_LABEL" :key="value" :value="value">{{ label }}</option>
                </select>
              </td>
              <td>
                <select
                  class="cell-input"
                  :value="element.evidenceDocumentId === null ? '' : String(element.evidenceDocumentId)"
                  :disabled="!canWrite"
                  :aria-label="`${element.name} 的证据文件`"
                  @change="onElementEvidence(element, $event)"
                >
                  <option value="">未挂文件</option>
                  <option v-for="doc in data.candidateDocuments" :key="doc.id" :value="String(doc.id)">
                    {{ doc.code ? `${doc.code} · ` : '' }}{{ doc.title }}
                  </option>
                </select>
              </td>
              <td>
                <input
                  class="cell-input"
                  type="text"
                  maxlength="1000"
                  :value="element.note ?? ''"
                  :disabled="!canWrite"
                  :aria-label="`${element.name} 的备注`"
                  @change="onElementNote(element, $event)"
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="ppap-footnote">
        自动核对只说明「系统里有没有这份东西」；版本对不对、内容全不全、是不是客户要的那一份，
        仍然要人看过再勾「就绪」。每次改动都记入审计。
      </p>
    </section>
  </template>
</template>

<style scoped>
.ppap-init { display: grid; gap: 12px; padding: 28px; }
.ppap-init h2 { margin: 0; font-size: 18px; }
.ppap-init p { margin: 0; max-width: 72ch; color: var(--muted); font-size: 13px; line-height: 1.9; }
.ppap-init-actions { display: flex; gap: 14px; align-items: end; flex-wrap: wrap; }
.ppap-init-actions label { display: grid; gap: 4px; font-size: 12px; color: var(--muted); }
.ppap-init-actions select { padding: 7px 10px; border: 1px solid var(--line); font: inherit; font-size: 13px; }

.ppap-head { display: flex; gap: 24px; align-items: center; justify-content: space-between; flex-wrap: wrap; margin-bottom: 12px; }
.ppap-meta { display: flex; gap: 14px; flex-wrap: wrap; }
.ppap-meta label { display: grid; gap: 4px; font-size: 12px; color: var(--muted); }
.ppap-meta select { padding: 6px 9px; border: 1px solid var(--line); font: inherit; font-size: 13px; }
.ppap-readiness { display: grid; gap: 4px; min-width: 260px; }
.ppap-bar { height: 12px; border: 1px solid var(--line); background: var(--paper); }
.ppap-bar i { display: block; height: 100%; background: var(--green); transition: width .2s; }
.ppap-readiness b { font-size: 13px; }
.ppap-readiness small { color: var(--warning); font-size: 12px; }

.ppap-table th:nth-child(2) { min-width: 180px; }
.ppap-table th:nth-child(3) { min-width: 240px; }
.ppap-table tbody tr.is-ready { background: #F1F9F4; }
.ppap-table tbody tr.is-not_applicable { opacity: .6; }
.ppap-table tbody tr.saving { opacity: .55; }
.ppap-name b { font-size: 13px; }
.ppap-auto { display: grid; gap: 3px; font-size: 12px; }
.ppap-auto i { font-style: normal; font-weight: 800; }
.ppap-auto i.ok { color: var(--green); }
.ppap-auto i.warn { color: var(--warning); }
.ppap-auto span { color: var(--muted); line-height: 1.6; }
.ppap-manual { color: var(--muted); font-size: 12px; }
.cell-input { width: 100%; min-width: 96px; padding: 5px 7px; border: 1px solid transparent; background: transparent; font: inherit; font-size: 12px; }
.cell-input:hover, .cell-input:focus { border-color: #C7D5E2; background: var(--white); outline: none; }
.ppap-footnote { margin: 10px 0 0; color: var(--muted); font-size: 12px; line-height: 1.8; }
</style>
